import { Component, OnInit, Input, ViewChild, SimpleChanges, forwardRef, HostListener } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { 
    IonContent, IonModal, IonPopover, IonFooter, IonToolbar, IonGrid, IonRow, IonCol, IonIcon, IonSelect, IonSelectOption, IonLabel, IonSpinner
} from '@ionic/angular/standalone';
import { ReactiveFormsModule, ControlContainer, FormGroupDirective, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { DemoozModalService } from '@demooz/services';
import { DemoozListComponent, DemoozButtonComponent, DemoozListItem, DemoozTagComponent } from '@demooz/components';

export class DemoozSelectItem extends DemoozListItem {}

@Component({
    selector: 'demooz-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    imports: [
        IonContent, IonModal, IonPopover, IonFooter, IonToolbar, IonGrid, IonRow, IonCol, IonIcon, IonSelect, IonSelectOption, IonLabel, IonSpinner,
        ReactiveFormsModule, TranslatePipe, AsyncPipe, 
        forwardRef(() => DemoozListComponent), forwardRef(() => DemoozButtonComponent), forwardRef(() => DemoozTagComponent)
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ]
})
export class DemoozSelectComponent  implements OnInit {

    @Input({alias : 'label', required : false}) labelSelect: string = '';
    @Input({alias : 'icon', required : false}) iconSelect: string = '';
    @Input({alias : 'formName', required : false}) formName: string|number = '';
    @Input({alias : 'errorText', required : false}) errorText: string = this.translateService.instant('MAIN.MSG_ERROR_SELECT_MANDATORY');
    @Input({alias : 'disabled', required : false}) disabled: boolean = false;
    @Input({alias : 'items', required : false}) items: Array<DemoozSelectItem>;   
    @Input({alias : 'maxItemsDisplayed', required : false}) maxItemsDisplayed: number = 100;
    @Input({alias : 'itemsSelected', required : false}) itemsSelected: Map<number|string|boolean, DemoozSelectItem> = new Map();
    @Input({alias : 'multiple', required : false}) multiple: boolean = false; 
    @Input({alias : 'displayItemsColors', required : false}) displayItemsColors: boolean = false;
    @Input({alias : 'displayItemsSelected', required : false}) displayItemsSelected: boolean = false;
    @Input({alias : 'autoClose', required : false}) autoClose: boolean = true;
    @Input({alias : 'noMargin', required : false}) noMargin: boolean = false;   
    @Input({alias : 'textAlign', required : false}) textAlign: 'left'|'right' = 'right';
    @Input({alias : 'withSearch', required : false}) withSearch: boolean = true;
    @Input({alias : 'interface', required : false}) interface: 'modal'|'popover' = 'modal';

    @ViewChild('modalList') modalList: IonModal;
    @ViewChild('demoozListPopover') demoozListPopover: DemoozListComponent;
    @ViewChild('demoozListModal') demoozListModal: DemoozListComponent;
    @ViewChild('popoverSelect') popoverSelect: IonPopover;
    
    @HostListener('window:resize', ['$event'])
    handleWindowResize(event: any){    
        if(this.interface === 'popover' && this.popoverSelect.isCmpOpen){
            this.popoverSelect.dismiss();
        }
    }
    
    itemsSelectedCopy: Map<number|string|boolean, DemoozSelectItem> = new Map();    
    initCount: boolean = false;
    isSearchingText: boolean = false;
    searchDefault: string = '';
    hideListModal: boolean = false;
    isRequired: boolean = false;
    
    updateStatusChange: Subscription;
       
    constructor(
        public translateService: TranslateService,
        public formGroupDirective: FormGroupDirective,
        private demoozModalService: DemoozModalService
    ) { }

    async ngOnInit() {        
        this.isRequired = this.formGroupDirective.form.controls[this.formName].hasValidator(Validators.required); 
        this.updateStatusChange = this.formGroupDirective.form.controls[this.formName].statusChanges.subscribe((s) => {
            this.isRequired = this.formGroupDirective.form.controls[this.formName].hasValidator(Validators.required);
        });             
    }
    
    async ngOnChanges(changes: SimpleChanges) {  
    
        if(changes['itemsSelected'] && changes['itemsSelected'].currentValue){
            this.itemsSelectedCopy = new Map(this.itemsSelected);
        }
                  
        if(changes['disabled'] && changes['disabled'].currentValue !== null){    
            if(this.disabled === true){
                this.formGroupDirective.form.controls[this.formName].disable({emitEvent: false});
            }
            else{
                this.formGroupDirective.form.controls[this.formName].enable({emitEvent: false});
            }
        }
    }
    
    ngOnDestroy() {           
        if(this.updateStatusChange){
            this.updateStatusChange.unsubscribe();
        }
    }

    compareWith(currentValue: any, compareValue: any) : boolean {
        return currentValue == compareValue;
    }

    openListModal(event:  any){     
        if(!this.disabled){
             
            this.itemsSelectedCopy.clear();

            let values = this.formGroupDirective.form.controls[this.formName].value;
            if(!Array.isArray(values)){
               values = [values]; 
            }
            values.forEach((value: string|number) => {
                let item = this.items.find(i => i.value === value);
                if(item){
                    this.itemsSelectedCopy.set(item.value, item);
                }
            });
            
            if(this.interface === 'popover'){
                this.popoverSelect.present(event);
            }
            else{
                this.demoozModalService.open(this.modalList);
            }
            
        }
    } 
    
    async canDismissListModal(data?: any, role?: string) {
        return role !== 'gesture';
    }
    
    async onDidDismissListModal($event: any){  
        this.demoozModalService.onDidDismiss($event);
    }

    async cancelChoices() {
        this.initCount = false;
        
        if(this.interface === 'popover'){
            await this.popoverSelect.dismiss();
        }
        else{
            await this.modalList.dismiss();
        }
        
    }
    
    getItemsSelected(): Array<DemoozSelectItem>{
        let itemsSelected: Array<DemoozSelectItem> = [];        
        let values = this.formGroupDirective.form.controls[this.formName].value;
        if(!Array.isArray(values)){
           values = [values]; 
        }            
        itemsSelected = this.items.filter(i => values.indexOf(i.value) !== -1);        
        return itemsSelected;
    }
    
    setItemsSelected(itemsSelected: Map<number|string|boolean, DemoozSelectItem>){
        let values:any = null;
        if(this.formGroupDirective.form.controls[this.formName]){            
            this.itemsSelectedCopy = itemsSelected;
            values = [];           
            itemsSelected.forEach((item) => {
                values.push(item.value);                
            });
            if(!this.multiple){            
                values = (values.length > 0 ? values[0] : null);   
            }  
            this.formGroupDirective.form.controls[this.formName].setValue(values);
            this.formGroupDirective.form.controls[this.formName].markAsTouched();
        }
    }

    validateChoices() {
        let itemsSelected = (this.interface === 'popover' ? this.demoozListPopover.getItemsSelected() : this.demoozListModal.getItemsSelected());
        this.setItemsSelected(itemsSelected);
        this.initCount = false;
        
        if(this.interface === 'popover'){
            this.popoverSelect.dismiss();
        }
        else{
            this.modalList.dismiss();
        }        
    }
        
    unselectItem(item: DemoozSelectItem){ 
        if(!this.disabled){     
            let values = this.formGroupDirective.form.controls[this.formName].value;
            if(!this.multiple){        
                values = null;
            }
            else{
                let indexValue = values.indexOf(item.value);
                if(indexValue !== -1){
                    values.splice(indexValue, 1);
                }
            }
            this.formGroupDirective.form.controls[this.formName].setValue(values); 
        }
    }
    
    getCountItemsSelected(): number{               
        let itemsSelected = (this.interface === 'popover' ? this.demoozListPopover?.getItemsSelected() : this.demoozListModal?.getItemsSelected()); 
        let countItems: number = itemsSelected?.size || 0;  
        
        if(!this.displayItemsSelected && this.multiple && itemsSelected){  
            let values = this.formGroupDirective.form.controls[this.formName].value;
            if(!Array.isArray(values)){
               values = [values]; 
            }
            if(itemsSelected){
                let cpt = 0;            
                itemsSelected.forEach(i => {
                    if(values.indexOf(i.value) === -1){
                        cpt++;
                    }
                });            
                countItems = cpt;
            }
        }   
        
        return countItems;
    }
    
    onSelectItem(items: Map<number|string|boolean, DemoozListItem>) {
        this.initCount = true;
        if(!this.multiple && this.autoClose && items.size > 0){
            this.validateChoices();
        }
    }    
    
    async onSearchEnd(itemsFounded: Array<DemoozListItem>){ 
        if(itemsFounded.length > 0){            
            let items = (itemsFounded.length === 1 ? itemsFounded : itemsFounded.filter(i => i.label.trim().match(new RegExp('^'+this.searchDefault+'$','gi'))));
            if(items.length === 1){                
                let itemsSelected = new Map(this.itemsSelectedCopy);
                itemsSelected.set(items[0].value, items[0]);
                this.setItemsSelected(itemsSelected);
                await this.cancelChoices();
                this.searchDefault = '';
            }            
        } 
        this.isSearchingText = false;            
        this.hideListModal = false; 
    }
}
